import { isDirectoryEntry, isFileEntry } from './fileSystemEntry';
import readDirectoryEntries from './readDirectoryEntries';

// ----------------------------------------------------------------------------

export type TypeCollectedFileEntry = {
    entry: FileSystemFileEntry;
    // Directory prefix the file was found under ('' for a file dropped at the root).
    path: string;
};

export type TypeCollectFileEntriesOptions = {
    fnProgress?: (found: number) => void;
    /*
     * Stops the walk. The readEntries batch already in flight still resolves —
     * there is no way to cancel one — but no further batch is started, so the walk
     * ends within one batch even inside a single huge directory. The caller decides
     * what an interrupted walk means by checking the signal itself; what comes back
     * is what had been found so far.
     */
    signal?: AbortSignal;
};

const PROGRESS_INTERVAL_MS = 100;

// ----------------------------------------------------------------------------

/*
 * Walks a dropped tree and returns the file entries in it, without turning a
 * single one into a File.
 *
 * Enumerating is cheap; materializing is not — entry.file() stats the file — so
 * the two are kept apart. That lets a caller count what was dropped, ask the
 * user about it, and report progress off the walk alone, paying for the File
 * objects only once it knows it wants them.
 *
 * The walk is iterative over an index-advanced queue rather than recursive:
 * shift()ing a queue that holds one item per dropped file is quadratic, which
 * on a ten-thousand-file folder costs more than the directory reads it is there
 * to sequence.
 */
async function collectFileEntries(
    rootEntries: FileSystemEntry[],
    options: TypeCollectFileEntriesOptions = {},
): Promise<TypeCollectedFileEntry[]> {
    const collected: TypeCollectedFileEntry[] = [];
    const queue: { entry: FileSystemEntry; path: string }[] = rootEntries.map((entry): any => ({ entry, path: '' }));

    let cursor = 0;
    let lastProgressAt = performance.now();

    while (cursor < queue.length) {
        if (options.signal?.aborted === true) {
            return collected;
        }

        const { entry, path } = queue[cursor];
        cursor += 1;

        if (isFileEntry(entry)) {
            collected.push({ entry, path });

            // Throttled: a flat folder would otherwise call back once per file.
            if (performance.now() - lastProgressAt >= PROGRESS_INTERVAL_MS) {
                options.fnProgress?.(collected.length);
                lastProgressAt = performance.now();
            }

            continue;
        }

        if (isDirectoryEntry(entry)) {
            const children = await readDirectoryEntries(entry, options.signal);
            const nextPath = `${path}${entry.name}/`;

            for (const child of children) {
                queue.push({ entry: child, path: nextPath });
            }
        }
    }

    options.fnProgress?.(collected.length);

    return collected;
}

// ----------------------------------------------------------------------------

export default collectFileEntries;
